import { useState, useEffect, useCallback } from 'react'
import { DanceClass, Schedule, apiService } from '../core'
import { isValidScheduleTime } from '../core'

interface UseScheduleDataOptions {
  danceClass: DanceClass | null
  isDemo?: boolean
}

interface UseScheduleDataReturn {
  // All schedules for the class (filtered by class and demo type)
  allClassSchedules: Schedule[]
  
  // Schedules for a specific date (with time validation)
  schedulesForDate: (date: string) => Schedule[]
  
  // Available dates with valid schedules
  availableDates: Map<string, Schedule[]>
  
  // Loading states
  isLoading: boolean
  error: string | null
  
  // Refresh function
  refresh: () => Promise<void>
}

/**
 * Custom hook to manage schedule data with caching and optimization
 * Eliminates redundant API calls between InlineDateCalendar and TimeSlotSelector
 */
export function useScheduleData({ 
  danceClass, 
  isDemo = false
}: UseScheduleDataOptions): UseScheduleDataReturn {
  const [allSchedules, setAllSchedules] = useState<Schedule[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch schedules from API with caching
  const fetchSchedules = useCallback(async (forceRefresh: boolean = false) => {
    if (!danceClass) {
      setAllSchedules([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const schedules = await apiService.getSchedules(undefined, forceRefresh)
      
      // Filter schedules for the specific class and demo type
      const filteredSchedules = schedules
        .filter((s: any) => s.classId === danceClass.id && s.isActive && s.isDemo === isDemo)
        .map((s: any) => {
          // Use the new currentEnrollment field from backend
          const currentEnrollment = s.currentEnrollment || 0
          const maxCapacity = s.maxCapacity || 0
          const remainingSpots = Math.max(0, maxCapacity - currentEnrollment)
          const isFullyBooked = remainingSpots === 0
          
          return {
            id: s.id,
            classId: s.classId,
            className: s.className,
            specificDate: s.specificDate,
            startTime: s.startTime,
            endTime: s.endTime,
            isDemo: s.isDemo,
            maxCapacity,
            currentEnrollment,
            remainingSpots,
            isFullyBooked,
            isActive: s.isActive,
            createdAt: s.createdAt
          }
        })

      setAllSchedules(filteredSchedules)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load schedules. Please try again after some time.'
      setError(errorMessage)
      setAllSchedules([])
    } finally {
      setIsLoading(false)
    }
  }, [danceClass?.id, isDemo])

  // Get schedules for a specific date with time validation
  const schedulesForDate = useCallback((date: string): Schedule[] => {
    return allSchedules.filter(schedule => 
      schedule.specificDate === date && 
      isValidScheduleTime(schedule.specificDate, schedule.startTime)
    )
  }, [allSchedules])

  // Generate available dates with valid schedules
  const availableDates = useCallback((): Map<string, Schedule[]> => {
    const dates = new Map<string, Schedule[]>()
    
    // Group schedules by date
    allSchedules.forEach(schedule => {
      if (isValidScheduleTime(schedule.specificDate, schedule.startTime)) {
        const date = schedule.specificDate
        if (!dates.has(date)) {
          dates.set(date, [])
        }
        dates.get(date)!.push(schedule)
      }
    })

    return dates
  }, [allSchedules])

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchSchedules(true) // Force refresh
  }, [fetchSchedules])

  // Initial fetch and refetch when dependencies change
  useEffect(() => {
    fetchSchedules()
  }, [fetchSchedules])


  return {
    allClassSchedules: allSchedules,
    schedulesForDate,
    availableDates: availableDates(),
    isLoading,
    error,
    refresh
  }
}
